import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { AudioCard } from './AudioCard';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const audios = [{
  number: '01',
  title: 'The Transfer Breath',
  subtitle: 'For the moment right before you attempt the put-down.',
  duration: '2 min',
  audioUrl: 'https://cdn.magicpatterns.com/uploads/77yNZdXxRNR3yeUrnEz22t/1_The_Transfer_Breath_English_music.mp4'
}, {
  number: '02',
  title: 'The Failed Transfer Reset',
  subtitle: "For when it didn't work. Again.",
  duration: '3 min',
  audioUrl: 'https://cdn.magicpatterns.com/uploads/uprRNL3S913fXT9icjBdyH/2_The_Failed_Transfer_Reset_English_music.mp4'
}, {
  number: '03',
  title: 'The Guilt Interrupt',
  subtitle: 'For 2am, when your brain turns against you.',
  duration: '3 min',
  audioUrl: 'https://cdn.magicpatterns.com/uploads/7qq7uyCkcVzwRS6sQQqfZB/3The Guilt Interrupt_English_music.mp4'
}, {
  number: '04',
  title: 'The Contact Nap Permission',
  subtitle: "For when you need someone to tell you it's okay.",
  duration: '2 min',
  audioUrl: 'https://cdn.magicpatterns.com/uploads/41LzxkFrSt81L7kCmD4ymp/4The Contact Nap Permission_English_music.mp4'
}, {
  number: '05',
  title: 'The Overstimulation Exit',
  subtitle: "For when you've hit your sensory limit.",
  duration: '4 min',
  audioUrl: 'https://cdn.magicpatterns.com/uploads/tTkDEVeCACavbR1PFh8rrZ/5The Overstimulation Exit-English_music.mp4'
}, {
  number: '06',
  title: 'The Partner Bridge',
  subtitle: 'For before a difficult conversation.',
  duration: '3 min',
  audioUrl: 'https://cdn.magicpatterns.com/uploads/wxFbhPxcoedK3mPCV1mzMw/6The Partner Bridge_English_music.mp4'
}, {
  number: '07',
  title: 'The Morning Anchor',
  subtitle: 'For the first 3 minutes of the day.',
  duration: '3 min',
  audioUrl: 'https://cdn.magicpatterns.com/uploads/tE7UfmxkhcfswWGjYjyFqM/7The Morning Anchor_English_music.mp4'
}, {
  number: '08',
  title: 'The Nap Window Prep',
  subtitle: 'For the 5 minutes before you attempt sleep.',
  duration: '2 min',
  audioUrl: 'https://cdn.magicpatterns.com/uploads/mPj9FzMANrTMgKWjNyzfAv/8The Nap Window Prep-English_music.mp4'
}, {
  number: '09',
  title: 'The "I Can\'t Do This" Response',
  subtitle: "For the moment you actually can't.",
  duration: '4 min',
  audioUrl: "https://cdn.magicpatterns.com/uploads/xmTd2uzrinSDmVeEdyAMVK/9The _I Can't Do This_ Response_English_music.mp4"
}, {
  number: '10',
  title: 'The End of Day Release',
  subtitle: 'For when the day is finally over.',
  duration: '5 min',
  audioUrl: 'https://cdn.magicpatterns.com/uploads/o9QnG7BgdzFSTA4xv9Fhtt/10The End of Day Release-English_music.mp4'
}];
export function WhatsInsideSection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  useEffect(() => {
    if (activeIndex !== null && scrollRef.current) {
      const activeElement = scrollRef.current.children[activeIndex] as HTMLElement;
      if (activeElement) {
        activeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [activeIndex]);
  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const amount = 180;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -amount : amount,
        behavior: 'smooth'
      });
    }
  };
  return <section className="py-20 relative overflow-hidden" data-id="element-231">
      {/* Subtle background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl h-[300px] bg-copper/5 rounded-[100%] blur-[80px] pointer-events-none" data-id="element-232" />

      <div className="text-center px-6 mb-8 relative z-10" data-id="element-233">
        <motion.span initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-copper opacity-70 text-xs tracking-[0.2em] uppercase font-semibold mb-3 block" data-id="element-234">
          ✦ The Collection ✦
        </motion.span>

        <motion.h2 initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="font-playfair text-charcoal text-4xl md:text-5xl font-medium mb-4" data-id="element-235">
          What's <span className="italic text-copper" data-id="element-236">Inside</span>
        </motion.h2>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-sm border border-copper/20 rounded-full px-4 py-1.5 shadow-sm" data-id="element-237">
          <span className="relative flex h-2 w-2" data-id="element-238">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-copper opacity-40" data-id="element-239"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-copper opacity-80" data-id="element-240"></span>
          </span>
          <span className="font-inter text-charcoal text-xs font-medium opacity-80 tracking-wide uppercase" data-id="element-241">
            Tap to preview · 20s samples
          </span>
        </motion.div>
      </div>

      {/* Carousel container */}
      <div className="relative z-10" data-id="element-242">
        {/* Scroll arrows — visible on hover / desktop */}
        <button onClick={() => scroll('left')} className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/90 backdrop-blur-md shadow-[0_4px_15px_rgba(201,166,107,0.2)] border border-copper/20 items-center justify-center hover:bg-white hover:scale-105 transition-all" aria-label="Scroll left" data-id="element-243">
          <ChevronLeftIcon className="w-5 h-5 text-copper" data-id="element-244" />
        </button>
        <button onClick={() => scroll('right')} className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/90 backdrop-blur-md shadow-[0_4px_15px_rgba(201,166,107,0.2)] border border-copper/20 items-center justify-center hover:bg-white hover:scale-105 transition-all" aria-label="Scroll right" data-id="element-245">
          <ChevronRightIcon className="w-5 h-5 text-copper" data-id="element-246" />
        </button>

        {/* Scrollable row */}
        <div ref={scrollRef} className="flex gap-3 overflow-x-auto px-6 py-4 scrollbar-hide snap-x snap-mandatory" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-247">
          {audios.map((audio, index) => <div key={audio.number} className="snap-start" data-id="element-248">
              <AudioCard index={index} number={audio.number} title={audio.title} subtitle={audio.subtitle} duration={audio.duration} audioUrl={audio.audioUrl} isActive={activeIndex === index} isDimmed={activeIndex !== null && activeIndex !== index} onToggle={() => setActiveIndex(activeIndex === index ? null : index)} onFinish={() => {
            if (index < audios.length - 1) {
              setActiveIndex(index + 1);
            } else {
              setActiveIndex(null);
            }
          }} data-id="element-249" />
            </div>)}
        </div>
      </div>
    </section>;
}